// 전투 액션 VFX 묶음(2026-10-27, docs/ACTION-VFX-REF.md): 레퍼런스 수준의 굵은 회전 베기 고리 · 타격 방사선 · 돌진 잔상 · 강타 파편.
// CombatFx가 만들어 자기 그룹에 붙이고 이벤트를 넘긴다(렌더 배선은 그대로). 칼날 리본(기본 1~4타)은 SlashFx가 그린다.
// 드로우콜: 고리 1 + 방사선 1 + 잔상 1. 프레임 루프·이벤트 처리 안에서 객체를 만들지 않는다(파라미터 객체 1개 재사용).
// 시간: 모듈은 초(영웅 시계 — 히트스톱·일시정지에 멈춘다), 잔상은 영웅 시계 틱.
import * as THREE from 'three/webgpu';
import { PALETTE } from '../../shared/config';
import { DodgeTrail } from '../dodgeTrail';
import type { BatchModule } from '../modules/batch';
import { createNoiseTexture } from '../modules/noise';
import { makeParams, type FxParams } from '../modules/types';
import type { ParticleFx } from '../particles';
import type { SlashFx } from '../slashes';
import { HitBurstModule, HIT_RAYS_MAX } from './hitBurst';
import { SlashRingModule } from './slashRing';
import { AFTERIMAGE, HIT_BURST, LOW_SCALE, RING_SHAPES, type RingShape } from './timing';

const CRIMSON = PALETTE.crimson;
/** 고리 심(흰 금빛) */
const HOT = 0xffe6b0;
/** 방사선 심(흰) */
const WHITE = 0xffffff;
const STONE = 0x7d6c58;

export interface ActionVfxStats { rings: number; bursts: number; afterimage: boolean; drawCalls: number; pending: number }

/** 이벤트 처리 중 띄운 효과를 다음 update까지 미뤄 두는 칸 수(한 프레임에 고리·방사선이 이보다 많으면 오래된 것부터 바로 띄운다) */
const PENDING_CAP = 24;

/** FxParams 필드 복사(할당 없음) */
function copyParams(d: FxParams, s: Readonly<FxParams>): void {
  d.x = s.x; d.y = s.y; d.z = s.z; d.yaw = s.yaw; d.color = s.color; d.color2 = s.color2; d.scale = s.scale; d.length = s.length; d.width = s.width;
  d.life = s.life; d.intensity = s.intensity; d.seed = s.seed; d.roll = s.roll; d.start = s.start; d.sweep = s.sweep; d.tail = s.tail; d.from = s.from;
  d.variant = s.variant; d.ahead = s.ahead; d.age0 = s.age0;
}

class Pending {
  readonly p: FxParams = makeParams();
  mod: BatchModule | null = null;
  /** 이벤트가 난 영웅 시계(틱) */
  at = 0;
}

export class ActionVfx {
  readonly group = new THREE.Group();
  readonly rings: SlashRingModule;
  readonly bursts: HitBurstModule;
  private readonly after: DodgeTrail;
  private readonly noise: THREE.DataTexture;
  private readonly p: FxParams = makeParams();
  private scale = 1;
  private reduce = false;
  private budgetTick = -1;
  private bBurst = 0;
  private seedN = 0;
  /** 라이라는 고리·잔상을 내지 않고 방사선만 청록 가장자리로 */
  private lyra = false;
  private accent: number = CRIMSON;
  /**
   * 이벤트 처리 중에 띄운 고리·방사선은 바로 슬롯에 넣지 않고 여기 두었다가 update에서 모듈을 먼저 진행한 뒤 넣는다
   * (렌더는 이벤트 → update 순서라, 바로 넣으면 첫 프레임 dt만큼 늙어 느린 프레임에서 0.11초 방사선이 안 보였다 — Codex P2).
   * 늦게 처리된 만큼(영웅 시계 now − 이벤트 시각)만 나이로 준다. skillModules의 "이벤트 처리 앞 갱신"과 같은 결과
   */
  private readonly pending: Pending[] = [];
  private pendingN = 0;
  /** 지금 처리 중인 이벤트의 영웅 시계(틱). CombatFx가 이벤트마다 at()으로 알린다 */
  private evNow = 0;

  constructor(private readonly slashes: SlashFx, private readonly particles: ParticleFx) {
    this.group.name = 'action-vfx';
    this.noise = createNoiseTexture();
    this.rings = new SlashRingModule(this.noise);
    this.bursts = new HitBurstModule();
    this.after = new DodgeTrail({
      life: AFTERIMAGE.life, gap: 0.3, y0: AFTERIMAGE.y0, y1: AFTERIMAGE.y1, sampleTicks: AFTERIMAGE.activeTicks - 12, activeTicks: AFTERIMAGE.activeTicks,
      body: [0.92, 0.14, 0.22], core: [1, 0.86, 0.62], opacity: 0.8,
    });
    this.group.add(this.rings.group, this.bursts.group, this.after.mesh);
    for (let i = 0; i < PENDING_CAP; i++) this.pending.push(new Pending());
  }

  /** 지금 처리하는 이벤트의 영웅 시계(틱) */
  at(now: number): this { this.evNow = now; return this; }

  /** 모듈에 띄울 것을 미뤄 둔다(가득 차면 가장 오래된 것을 지금 띄우고 자리를 쓴다) */
  private queue(mod: BatchModule, p: Readonly<FxParams>): void {
    if (this.pendingN >= PENDING_CAP) {
      const old = this.pending[0];
      old.mod?.spawn(old.p);
      for (let i = 1; i < this.pendingN; i++) this.pending[i - 1] = this.pending[i];
      this.pending[this.pendingN - 1] = old;
      this.pendingN--;
    }
    const slot = this.pending[this.pendingN++];
    copyParams(slot.p, p);
    slot.mod = mod;
    slot.at = this.evNow;
  }

  setHeroId(lyra: boolean, accent: number): void { this.lyra = lyra; this.accent = accent; }

  setScale(s: number): void { this.scale = s; this.budgetTick = -1; }

  setReduceFlash(on: boolean): void {
    this.reduce = on;
    this.rings.setGain(on ? 0.6 : 1);
    this.bursts.setGain(on ? HIT_BURST.reduceGain : 1);
    this.after.gain.value = on ? 0.6 : 1;
  }

  private get low(): boolean { return this.scale < LOW_SCALE; }

  private nextSeed(): number { this.seedN = (this.seedN + 1) % 997; return (this.seedN * 0.6180339) % 1; }

  /** 굵은 고리 하나. r: 바깥 반경(0이면 shape.radius), yaw: 기울기 축(방향), dir: 쓸어 지나가는 방향(±1) */
  ring(sh: RingShape, x: number, y: number, z: number, yaw: number, r: number, dir = 1, start = 0): void {
    if (this.scale <= 0 || this.lyra) return;
    const p = this.p;
    p.x = x; p.y = y + sh.y; p.z = z; p.yaw = yaw;
    p.color = CRIMSON; p.color2 = HOT;
    p.scale = 1; p.length = r > 0 ? r : sh.radius; p.width = sh.band;
    p.life = sh.life; p.tail = sh.draw; p.intensity = sh.intensity;
    p.roll = sh.roll; p.start = start; p.sweep = sh.sweep * dir;
    p.seed = this.nextSeed(); p.variant = this.low ? 1 : 0; p.ahead = 0; p.from = 0; p.age0 = 0;
    this.queue(this.rings, p);
  }

  /** 기본 공격 참격(SlashFx.onTrail·onSlash → CombatFx). 마무리 4타만 굵은 고리 + 가로지르는 직선 베기 */
  basicSlash(step: number, x: number, y: number, z: number, yaw: number, radius: number, now: number): void {
    if (step !== 3 || this.scale <= 0 || this.lyra) return;
    const sh = RING_SHAPES.finisher;
    // 기울기 축은 휘두른 방향에서 조금 비켜(매번 다르게) — 레퍼런스처럼 고리가 바닥에 비스듬히 눕는다
    const tiltYaw = yaw + 0.6 + (this.nextSeed() - 0.5) * 0.8;
    this.ring(sh, x, y, z, tiltYaw, radius > 0 ? radius : 3.6, 1, yaw);
    if (sh.cross) {
      const ang = yaw + Math.PI / 2 + (this.nextSeed() - 0.5) * 0.6;
      const len = (radius > 0 ? radius : 3.6) * 2.1;
      this.slashes.crossLine(x - Math.sin(ang) * len * 0.5, y + 0.9, z - Math.cos(ang) * len * 0.5, ang, len, 0.5, now, 12);
    }
  }

  /** 칼날 폭풍 회차 k(0..5). 마지막 회차는 판정 반경 큰 고리 */
  stormPulse(x: number, y: number, z: number, yaw: number, k: number, radius: number): void {
    const dir = (k & 1) === 1 ? -1 : 1;
    const tiltYaw = yaw + k * 1.05;
    if (k >= 5) this.ring(RING_SHAPES.stormFinal, x, y, z, tiltYaw, radius, dir, yaw);
    else this.ring(RING_SHAPES.storm, x, y, z, tiltYaw, 0, dir, yaw + k * 0.9);
  }

  /** 천공 올려베기 판정: 앞쪽에 세로로 선 고리(아래에서 위로 쓸어 올린다) */
  launcher(x: number, y: number, z: number, yaw: number): void {
    // planeFrame: yaw 방향 + roll 1.25 → 거의 세로 평면. 시작각 -0.9rad(앞 아래)에서 위로
    this.ring(RING_SHAPES.launcher, x + Math.sin(yaw) * 0.6, y, z + Math.cos(yaw) * 0.6, yaw, 0, 1, -0.9);
  }

  /** 붉은 낙화 착지: 바닥과 평행한 큰 고리 + 돌 파편 */
  fallLand(x: number, y: number, z: number, yaw: number, radius: number): void {
    this.ring(RING_SHAPES.fall, x, y, z, yaw, radius, 1, yaw);
    if (!this.lyra && this.scale > 0) this.particles.debris(x, z, yaw, Math.round(14 * this.scale), STONE);
  }

  /** 공중 연타(Ev.Juggle): 뜬 몹 둘레 작은 기울어진 고리 */
  juggle(x: number, y: number, z: number, yaw: number): void {
    this.ring(RING_SHAPES.juggle, x, Math.max(0.6, y + 0.8), z, yaw, 0, 1, yaw);
  }

  /** 잔상 시작(돌진). now: 영웅 시계 틱, 영웅 위치 */
  afterimage(now: number, x: number, z: number): void {
    if (this.scale <= 0 || this.lyra) return;
    this.after.begin(now, x, z);
  }

  /**
   * 타격(Ev.Hit, 영웅이 때린 것). cx,cy,cz: 접촉점을 카메라 쪽으로 당긴 자리, heavy: 치명·마무리·스킬, multi: 다단 중간타.
   * 판정 순간당 상한(HIT_BURST.perTick × 품질). 돌 파편은 처치(CombatFx.kill)·낙화 착지만 낸다(고체 입자 풀은 성인 모드 방울과 같이 쓴다)
   */
  hit(tick: number, cx: number, cy: number, cz: number, dir: number, heavy: boolean, multi: boolean): void {
    if (this.scale <= 0) return;
    if (tick !== this.budgetTick) {
      this.budgetTick = tick;
      this.bBurst = this.scale >= 1 ? HIT_BURST.perTick : Math.max(1, Math.round(HIT_BURST.perTick * this.scale));
    }
    if (this.bBurst <= 0) return;
    this.bBurst--;
    const B = HIT_BURST, p = this.p;
    const lenK = this.reduce ? B.reduceLen : 1;
    let rays: number = heavy ? B.raysHeavy : B.rays;
    if (multi) rays = 4;
    if (this.low) rays = Math.max(4, Math.round(rays * 0.6));
    p.x = cx; p.y = cy; p.z = cz; p.yaw = dir;
    p.color = this.accent; p.color2 = WHITE;
    p.scale = 1; p.length = (heavy ? B.lenHeavy : B.len) * (multi ? 0.7 : 1) * lenK; p.width = heavy ? B.widthHeavy : B.width;
    p.life = heavy ? B.lifeHeavy : B.life; p.intensity = heavy ? 2.4 : 1.9;
    p.seed = this.nextSeed(); p.variant = Math.min(HIT_RAYS_MAX, rays); p.age0 = 0;
    this.queue(this.bursts, p);
  }

  /** 매 렌더 프레임. now: 영웅 시계 틱, dtSec: 영웅 시간 경과(초) */
  update(now: number, dtSec: number, camera: THREE.Camera, hx: number, hy: number, hz: number): void {
    this.bursts.setCamera(camera);
    // 이미 떠 있는 것을 먼저 진행하고, 이번 프레임에 받은 것은 늦게 처리된 만큼만 나이를 준다
    this.rings.update(dtSec);
    this.bursts.update(dtSec);
    for (let i = 0; i < this.pendingN; i++) {
      const q = this.pending[i];
      q.p.age0 = Math.max(0, (now - q.at) / 60);
      q.mod?.spawn(q.p);
      q.mod = null;
    }
    this.pendingN = 0;
    this.after.update(now, hx, hy, hz);
  }

  stats(): ActionVfxStats {
    return { rings: this.rings.activeCount, bursts: this.bursts.activeCount, afterimage: this.after.isActive, drawCalls: this.rings.drawCalls + this.bursts.drawCalls + (this.after.mesh.visible ? 1 : 0), pending: this.pendingN };
  }

  prewarm(on: boolean): void {
    if (on) {
      // 컴파일용 하나씩(보이게 둔다)
      this.ring(RING_SHAPES.storm, 0, 0, 0, 0, 0);
      this.pendingN = 0;
      this.rings.spawn(this.p);
      this.p.variant = 6; this.p.length = 1; this.p.life = 0.1;
      this.bursts.spawn(this.p);
    }
    this.rings.prewarm(on);
    this.bursts.prewarm(on);
    this.after.mesh.visible = on;
    if (!on) this.clear();
  }

  clear(): void {
    this.pendingN = 0;
    for (const q of this.pending) q.mod = null;
    this.rings.clear();
    this.bursts.clear();
    this.after.clear();
    this.budgetTick = -1;
  }

  dispose(): void {
    this.rings.dispose();
    this.bursts.dispose();
    this.after.dispose();
    this.noise.dispose();
  }
}
